/**
 * Reglas puras de presentación de "Mi horario" (008 · T096/T097; contracts/ui.md §2.1–§2.2,
 * research R-25).
 *
 * **Solo eligen textos** a partir de lo que ya decidió el servidor (`GET /attendance/days`): el
 * estado de cada día (`display_status`), los minutos, el plan, el feriado y la ausencia. Aquí no se
 * evalúa ninguna regla de asistencia (wiring §11.4): el único cómputo propio es buscar el próximo
 * turno entre los días que ya llegaron y unir, por `operative_day`, el historial de marcas con los
 * días evaluados (research R-26: "Staff une por `operative_day` en el cliente").
 */
import {
  AlertTriangle,
  BadgeCheck,
  CalendarX,
  Check,
  CircleX,
  ClockAlert,
  Coffee,
  FileClock,
  FileText,
  Hourglass,
  LogOut,
  PartyPopper,
  Plane,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';

import type {
  DayDisplayStatus,
  StaffAttendanceDay,
  StaffDay,
  StaffDaysResponse,
  StaffShift,
} from '@fermin/contracts';

import { formatWeekdayDate, LOCALE, shiftDay, t } from '../../i18n';

// ---------------------------------------------------------------------------
// Horas de un turno
// ---------------------------------------------------------------------------

/** "08:00 – 17:00" (o "08:00 – 12:00 y 16:00 – 20:00" con turno partido). Horas locales de la sede. */
export function hoursOf(shifts: readonly Pick<StaffShift, 'start_time' | 'end_time'>[]): string {
  return shifts
    .map((shift) => t('schedule.hours.range', { start: shift.start_time, end: shift.end_time }))
    .join(t('schedule.hours.join'));
}

/** "miércoles 24" (día de calendario tal cual, sin zona). */
export function weekdayAndDay(day: string): string {
  const date = new Date(`${day}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return day;
  const weekday = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: 'UTC' }).format(
    date,
  );
  return `${weekday} ${date.getUTCDate()}`;
}

// ---------------------------------------------------------------------------
// Inicio · línea "Tu turno hoy" (§2.1)
// ---------------------------------------------------------------------------

export type TodayLineKind = 'SHIFT' | 'REST' | 'HOLIDAY' | 'ABSENCE';

export interface TodayLine {
  kind: TodayLineKind;
  /** Una o dos líneas (p. ej. "Hoy descansas" + "Próximo turno: …"). */
  lines: string[];
}

/** Primer día posterior a `today` con turno, entre los días que trajo la respuesta. */
export function nextShiftDay(data: StaffDaysResponse): StaffDay | null {
  return (
    data.days.find(
      (day) => day.day > data.today && day.plan.kind === 'SHIFT' && day.plan.shifts.length > 0,
    ) ?? null
  );
}

/**
 * Línea del Inicio o `null` si no hay nada que decir (sin horario, sin asignación hoy). Prioridad
 * igual a la del estado del día: ausencia de día completo > feriado > turno > descanso. En un
 * feriado con turno asignado se muestra el turno (el trabajador tiene que ir).
 */
export function todayLineOf(data: StaffDaysResponse): TodayLine | null {
  if (!data.has_schedule) return null;
  const today = data.days.find((day) => day.day === data.today);
  if (today === undefined) return null;

  if (today.absence !== null && !today.absence.partial) {
    return { kind: 'ABSENCE', lines: [t(`schedule.today.absence.${today.absence.type}`)] };
  }
  const shifts = today.plan.kind === 'SHIFT' ? today.plan.shifts : [];
  if (today.holiday !== null && shifts.length === 0) {
    return { kind: 'HOLIDAY', lines: [t('schedule.today.holiday', { name: today.holiday.name })] };
  }
  if (shifts.length > 0) {
    const lines = [
      t('schedule.today.shift', { hours: hoursOf(shifts), site: shifts[0]!.site.name }),
    ];
    const absence = today.absence;
    if (absence?.partial && absence.start_time !== null && absence.end_time !== null) {
      lines.push(
        t('schedule.today.partialPermission', { start: absence.start_time, end: absence.end_time }),
      );
    }
    return { kind: 'SHIFT', lines };
  }
  if (today.plan.kind === 'REST') {
    const lines = [t('schedule.today.rest')];
    const next = nextShiftDay(data);
    if (next !== null) {
      const label =
        next.day === shiftDay(data.today, 1)
          ? t('schedule.today.tomorrow', { date: weekdayAndDay(next.day) })
          : weekdayAndDay(next.day);
      lines.push(t('schedule.today.nextShift', { day: label, hours: hoursOf(next.plan.shifts) }));
    }
    return { kind: 'REST', lines };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Asistencia · badge del estado del día (§2.2)
// ---------------------------------------------------------------------------

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface StatusBadge {
  status: DayDisplayStatus;
  text: string;
  icon: LucideIcon;
  tone: StatusTone;
}

const STATUS_STYLE: Record<
  Exclude<DayDisplayStatus, 'NO_SCHEDULE'>,
  { icon: LucideIcon; tone: StatusTone }
> = {
  ON_TIME: { icon: Check, tone: 'success' },
  LATE: { icon: ClockAlert, tone: 'warning' },
  EARLY_LEAVE: { icon: LogOut, tone: 'warning' },
  LATE_AND_EARLY_LEAVE: { icon: ClockAlert, tone: 'warning' },
  INCOMPLETE: { icon: AlertTriangle, tone: 'warning' },
  ABSENT: { icon: CircleX, tone: 'danger' },
  ABSENT_JUSTIFIED: { icon: BadgeCheck, tone: 'info' },
  UNSCHEDULED_WORK: { icon: CalendarX, tone: 'neutral' },
  REST: { icon: Coffee, tone: 'neutral' },
  HOLIDAY: { icon: PartyPopper, tone: 'info' },
  PERMISSION: { icon: FileClock, tone: 'info' },
  VACATION: { icon: Plane, tone: 'info' },
  LICENSE: { icon: FileText, tone: 'info' },
  MEDICAL_LEAVE: { icon: Stethoscope, tone: 'info' },
  PENDING: { icon: Hourglass, tone: 'neutral' },
};

/**
 * Badge del día o `null` (día futuro, sin evaluación o "Sin horario": nada que mostrar). Una
 * tardanza o salida anticipada con justificación aprobada se lee "… justificada" (Info), sin
 * borrar el hecho (spec Clarifications).
 */
export function statusBadgeOf(day: StaffDay): StatusBadge | null {
  const evaluation = day.evaluation;
  if (evaluation === null || evaluation.display_status === 'NO_SCHEDULE') return null;
  const status = evaluation.display_status;
  const style = STATUS_STYLE[status];

  if (status === 'LATE' && evaluation.flags.includes('LATE_JUSTIFIED')) {
    return { status, text: t('schedule.status.LATE_JUSTIFIED'), icon: BadgeCheck, tone: 'info' };
  }
  if (status === 'EARLY_LEAVE' && evaluation.flags.includes('EARLY_LEAVE_JUSTIFIED')) {
    return {
      status,
      text: t('schedule.status.EARLY_LEAVE_JUSTIFIED'),
      icon: BadgeCheck,
      tone: 'info',
    };
  }

  let text: string;
  if (status === 'LATE') {
    text = t('schedule.status.LATE', { minutes: evaluation.late_minutes });
  } else if (status === 'EARLY_LEAVE') {
    text = t('schedule.status.EARLY_LEAVE', { minutes: evaluation.early_leave_minutes });
  } else if (status === 'LATE_AND_EARLY_LEAVE') {
    text = t('schedule.status.LATE_AND_EARLY_LEAVE', {
      late: evaluation.late_minutes,
      early: evaluation.early_leave_minutes,
    });
  } else {
    text = t(`schedule.status.${status}`);
  }
  return { status, text, icon: style.icon, tone: style.tone };
}

/** "Turno 08:00 – 17:00" o `null` si el día no tiene turno. */
export function shiftLineOf(day: StaffDay): string | null {
  if (day.plan.kind !== 'SHIFT' || day.plan.shifts.length === 0) return null;
  return t('schedule.day.shift', { hours: hoursOf(day.plan.shifts) });
}

// ---------------------------------------------------------------------------
// Asistencia · "Próximos días" (§2.2)
// ---------------------------------------------------------------------------

export const UPCOMING_DAYS = 7;

export interface UpcomingDay {
  day: string;
  label: string;
  text: string;
}

/** Los 7 días siguientes a hoy con su plan ("Sin horario asignado" si no hay nada). */
export function upcomingDaysOf(data: StaffDaysResponse): UpcomingDay[] {
  const byDay = new Map(data.days.map((day) => [day.day, day]));
  const result: UpcomingDay[] = [];
  for (let offset = 1; offset <= UPCOMING_DAYS; offset += 1) {
    const dayKey = shiftDay(data.today, offset);
    const day = byDay.get(dayKey);
    let text = t('schedule.upcoming.noSchedule');
    if (day !== undefined) {
      if (day.absence !== null && !day.absence.partial) {
        text = t(`schedule.status.${day.absence.type}`);
      } else if (day.plan.kind === 'SHIFT' && day.plan.shifts.length > 0) {
        text = t('schedule.upcoming.shift', {
          hours: hoursOf(day.plan.shifts),
          site: day.plan.shifts[0]!.site.name,
        });
      } else if (day.holiday !== null) {
        text = t('schedule.upcoming.holiday', { name: day.holiday.name });
      } else if (day.plan.kind === 'REST') {
        text = t('schedule.upcoming.rest');
      }
    }
    const label =
      offset === 1
        ? `${t('schedule.upcoming.tomorrow')} · ${weekdayAndDay(dayKey)}`
        : formatWeekdayDate(dayKey);
    result.push({ day: dayKey, label, text });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Asistencia · unión por día operativo (research R-26)
// ---------------------------------------------------------------------------

export interface MergeScheduleOptions {
  /**
   * Día más antiguo ya cargado del historial cuando quedan páginas por pedir: los días evaluados
   * más antiguos no se intercalan todavía (aparecerán al pedir "Ver días anteriores"). `null` si
   * el historial está completo.
   */
  oldestLoaded: string | null;
}

/**
 * Días del historial (con marcas) + días evaluados **sin** marcas que tienen algo que decir (falta,
 * descanso, feriado, vacaciones…) hasta hoy, en orden descendente. Un día sin marcas se pinta con
 * la lista vacía. "Sin horario" y los días futuros no se añaden.
 */
export function mergeScheduleDays(
  historyDays: readonly StaffAttendanceDay[],
  schedule: StaffDaysResponse | undefined,
  options: MergeScheduleOptions,
): StaffAttendanceDay[] {
  if (schedule === undefined || !schedule.has_schedule) return [...historyDays];
  const present = new Set(historyDays.map((day) => day.operative_day));
  const extra: StaffAttendanceDay[] = schedule.days
    .filter(
      (day) =>
        day.day <= schedule.today &&
        !present.has(day.day) &&
        day.evaluation !== null &&
        day.evaluation.display_status !== 'NO_SCHEDULE' &&
        (options.oldestLoaded === null || day.day >= options.oldestLoaded),
    )
    .map((day) => ({ operative_day: day.day, marks: [], worked_minutes: null }));
  if (extra.length === 0) return [...historyDays];
  return [...historyDays, ...extra].sort((a, b) =>
    a.operative_day < b.operative_day ? 1 : a.operative_day > b.operative_day ? -1 : 0,
  );
}

/** Día del horario por `operative_day` (solo si la empresa usa horarios para el trabajador). */
export function scheduleByDay(
  schedule: StaffDaysResponse | undefined,
): ReadonlyMap<string, StaffDay> {
  if (schedule === undefined || !schedule.has_schedule) return new Map();
  return new Map(schedule.days.map((day) => [day.day, day]));
}
